import * as Api from '@ViewModels';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';

export interface ILeadAIContext {
	data: string;
}

/** Query key for lead AI context - use this for cache invalidation */
export const getLeadAIContextQueryKey = (leadId: string, leadServedId?: string) => [
	'lead',
	leadId,
	'aiContext',
	leadServedId,
];

/**
 * Fetches and caches lead AI context data.
 * Data is cached for 10 minutes and stays in cache for 30 minutes.
 * Multiple components using the same leadId will share the same cached data.
 */
export function useLeadAIContext(leadId: string | undefined, leadServedId?: string) {
	const userSession = useUserSession();

	return useQuery<ILeadAIContext, Error>({
		queryKey: getLeadAIContextQueryKey(leadId || '', leadServedId),
		queryFn: async () => {
			if (!leadId) {
				throw new Error('Lead ID is required');
			}

			const queryParams = leadServedId ? `?leadServedId=${encodeURIComponent(leadServedId)}` : '';
			const response = await userSession.webServiceHelper.callWebServiceAsync<string>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `lead/${leadId}/AIContext${queryParams}` }),
				'GET'
			);

			const result = { data: response.value };
			console.groupCollapsed('[Coffee] AIContext');
			console.log(JSON.stringify(result, null, 2));
			console.groupEnd();
			return result;
		},
		enabled: !!leadId, // Only fetch when we have a leadId
		staleTime: 10 * 60 * 1000, // Data is fresh for 10 minutes
		gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes after last use
		retry: 1, // Only retry once on failure
		refetchOnWindowFocus: false, // Don't refetch when window regains focus
		refetchOnMount: false, // Don't refetch when component remounts if data is fresh
	});
}

/**
 * Hook to prefetch lead AI context data without subscribing to updates.
 * Use this in Queue to eagerly load data for upcoming leads.
 */
export function usePrefetchLeadAIContext() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	const prefetch = useCallback(
		async (leadId: string, leadServedId?: string) => {
			if (!leadId) return;

			// Check if data is already in cache and fresh
			const existingData = queryClient.getQueryData<ILeadAIContext>(getLeadAIContextQueryKey(leadId, leadServedId));
			if (existingData) return; // Already have fresh data

			await queryClient.prefetchQuery({
				queryKey: getLeadAIContextQueryKey(leadId, leadServedId),
				queryFn: async () => {
					const queryParams = leadServedId ? `?leadServedId=${encodeURIComponent(leadServedId)}` : '';
					const response = await userSession.webServiceHelper.callWebServiceAsync<string>(
						Api.ImpersonationBroker.composeApiUrl({ urlPath: `lead/${leadId}/AIContext${queryParams}` }),
						'GET'
					);
					const result = { data: response.value };
					console.groupCollapsed('[Coffee] AIContext');
					console.log(JSON.stringify(result, null, 2));
					console.groupEnd();
					return result;
				},
				staleTime: 10 * 60 * 1000,
			});
		},
		[queryClient, userSession]
	);

	return prefetch;
}

/**
 * Helper hook to get cached data without triggering a fetch.
 * @deprecated Use useLeadAIContext directly - it handles caching automatically
 */
export function useLeadAIContextData(leadId: string | undefined, leadServedId?: string) {
	const queryClient = useQueryClient();

	if (!leadId) return undefined;

	return queryClient.getQueryData<ILeadAIContext>(getLeadAIContextQueryKey(leadId, leadServedId));
}
